import { forwardRef, InputHTMLAttributes, useId } from 'react';

import cn from '@/utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  // Optional form-field affordances. When none are set, renders a bare styled
  // input (shadcn primitive behaviour) so it stays react-hook-form friendly.
  label?: string;
  error?: string;
  helperText?: string;
  // Layout classes belong on the wrapper, not the <input>: `className` is
  // merged onto the input itself, so `sm:col-span-2` passed there lands on a
  // non-grid-item and is inert.
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, id, className, wrapperClassName, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? (label ? generatedId : undefined);

    const input = (
      <input
        ref={ref}
        id={inputId}
        className={cn(
          // `text-base` (16px) below `sm`: iOS Safari zooms the viewport on
          // focus of any control under 16px and never zooms back out.
          // `min-w-0` so the input can shrink inside a narrow grid/flex cell
          // instead of forcing its column past the container width.
          // The responsive height step is a CSS custom property, not `sm:h-9`:
          // twMerge cannot dedupe a `sm:`-prefixed base class against a
          // caller's unprefixed `h-7`, so the base height would win at >=640px.
          // `h-[var(--input-h)]` conflicts with `h-7` on the same key and loses.
          'flex h-[var(--input-h)] w-full min-w-0 rounded-md border bg-background px-3 py-1 text-base shadow-sm transition-colors [--input-h:2.75rem] sm:[--input-h:2.25rem] sm:text-sm',
          'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error ? 'border-destructive focus-visible:ring-destructive' : 'border-input',
          className,
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && inputId ? `${inputId}-error` : undefined}
        {...props}
      />
    );

    // `wrapperClassName` counts as a reason to render the wrapper: without this
    // the bare-input path returned early and silently dropped the prop, so
    // layout classes like `sm:col-span-2` disappeared whenever the field had no
    // label/error/helper text.
    if (!label && !error && !helperText && !wrapperClassName) return input;

    return (
      <div className={cn('flex min-w-0 flex-col gap-1', wrapperClassName)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        {input}
        {error && (
          <p
            id={inputId ? `${inputId}-error` : undefined}
            className="text-xs text-destructive"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && <p className="text-xs text-muted-foreground">{helperText}</p>}
      </div>
    );
  },
);
Input.displayName = 'Input';
